export type Severity = "high" | "medium" | "low";

export type HeaderPair = {
  name: string;
  value: string;
};

export type SensitiveHit = {
  area: string;
  name: string;
  severity: Severity;
  reason: string;
};

export type NetworkEntry = {
  id: string;
  index: number;
  method: string;
  url: string;
  displayUrl: string;
  host: string;
  path: string;
  protocol: string;
  status: number;
  statusText: string;
  mimeType: string;
  startedDateTime: string;
  startOffset: number;
  time: number;
  timings: Record<string, number>;
  wait: number;
  size: number;
  cacheStatus: string;
  isError: boolean;
  isSlow: boolean;
  isThirdParty: boolean;
  sensitiveHits: SensitiveHit[];
};

export type DomainSummary = {
  host: string;
  requests: number;
  bytes: number;
  errors: number;
  slowest: number;
  thirdParty: boolean;
};

export type Finding = {
  id: string;
  severity: Severity;
  category: string;
  title: string;
  detail: string;
  evidence: string[];
  entryId?: string;
};

export type AnalysisMetrics = {
  totalRequests: number;
  totalBytes: number;
  totalSpan: number;
  errors: number;
  slowRequests: number;
  sensitiveRequests: number;
  thirdPartyRequests: number;
  domains: number;
  httpsRatio: number;
  riskLevel: Severity;
};

export type HarAnalysis = {
  sourceName: string;
  generatedAt: string;
  primaryHost: string;
  entries: NetworkEntry[];
  domains: DomainSummary[];
  findings: Finding[];
  metrics: AnalysisMetrics;
  report: string;
};

const sensitiveNameFragments = [
  "authorization",
  "cookie",
  "setcookie",
  "apikey",
  "api-key",
  "access_token",
  "refreshtoken",
  "refresh_token",
  "token",
  "jwt",
  "secret",
  "password",
  "passwd",
  "session",
  "client_secret",
  "privatekey",
  "private-key",
  "signature",
  "sig"
];

const timingKeys = ["blocked", "dns", "connect", "ssl", "send", "wait", "receive"];

export function analyzeHar(raw: string, sourceName = "pasted-har.json"): HarAnalysis {
  const root = parseJson(raw);
  const log = readRecord(root, "log");
  const version = readString(log, "version", "");
  const entriesValue = log.entries;

  if (!version || !Array.isArray(entriesValue)) {
    throw new Error("This does not look like a HAR file. Expected log.version and log.entries.");
  }

  const entryRecords = entriesValue.filter(isRecord);

  if (!entryRecords.length) {
    throw new Error("The HAR file has no readable log entries.");
  }

  const firstStart = Math.min(...entryRecords.map(readStartedMs).filter(Number.isFinite));
  const normalizedFirstStart = Number.isFinite(firstStart) ? firstStart : Date.now();
  const firstHost = extractHostFromEntry(entryRecords[0]);
  const primaryHost = firstHost || "unknown-host";
  const entries = entryRecords.map((entry, index) =>
    normalizeEntry(entry, index, normalizedFirstStart, primaryHost)
  );

  const domains = summarizeDomains(entries, primaryHost);
  const findings = buildFindings(entries);
  const metrics = buildMetrics(entries, domains, findings);
  const generatedAt = new Date().toISOString();
  const analysisCore = {
    sourceName,
    generatedAt,
    primaryHost,
    entries,
    domains,
    findings,
    metrics
  };

  return {
    ...analysisCore,
    report: createReport(analysisCore)
  };
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const precision = value >= 10 || unitIndex === 0 ? 0 : 1;
  return `${value.toFixed(precision)} ${units[unitIndex]}`;
}

export function formatMs(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "0 ms";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(ms < 10000 ? 1 : 0)} s`;
}

export function sanitizeUrl(rawUrl: string): string {
  try {
    const parsed = new URL(rawUrl);
    const params = new URLSearchParams(parsed.search);
    let changed = false;

    params.forEach((value, key) => {
      if (isSensitiveName(key) || looksSensitiveValue(value)) {
        params.set(key, "[redacted]");
        changed = true;
      }
    });

    parsed.search = changed ? params.toString() : parsed.search;
    return parsed.toString().replace(/%5Bredacted%5D/gi, "[redacted]");
  } catch {
    return truncateMiddle(rawUrl, 180);
  }
}

export function truncateMiddle(value: string, limit = 92): string {
  if (value.length <= limit) return value;
  const slice = Math.floor((limit - 3) / 2);
  return `${value.slice(0, slice)}...${value.slice(-slice)}`;
}

function parseJson(raw: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isRecord(parsed)) {
      throw new Error("HAR root is not an object.");
    }
    return parsed;
  } catch (error) {
    if (error instanceof Error && error.message !== "HAR root is not an object.") {
      throw new Error(`JSON parse failed: ${error.message}`);
    }
    throw error;
  }
}

function normalizeEntry(
  entry: Record<string, unknown>,
  index: number,
  firstStart: number,
  primaryHost: string
): NetworkEntry {
  const request = readRecord(entry, "request");
  const response = readRecord(entry, "response");
  const content = readRecord(response, "content");
  const timingsRecord = readRecord(entry, "timings");
  const url = readString(request, "url", "about:blank");
  const parsedUrl = parseUrl(url);
  const requestHeaders = readHeaders(request.headers);
  const responseHeaders = readHeaders(response.headers);
  const timings = readTimings(timingsRecord);
  const totalTiming = sumPositive(Object.values(timings));
  const requestTime = readNumber(entry, "time", totalTiming);
  const time = requestTime > 0 ? requestTime : totalTiming;
  const startMs = readStartedMs(entry);
  const startOffset = Number.isFinite(startMs) ? Math.max(0, startMs - firstStart) : 0;
  const status = Math.max(0, Math.round(readNumber(response, "status", 0)));
  const size = estimateTransferSize(response, content);
  const queryPairs = readNameValueList(request.queryString).concat(queryFromUrl(url));
  const postData = readRecord(request, "postData");
  const postPairs = readNameValueList(postData.params);
  const postText = readString(postData, "text", "");
  const sensitiveHits = findSensitiveHits(requestHeaders, responseHeaders, queryPairs, postPairs, postText);
  const host = parsedUrl.host || "unknown-host";
  const cacheStatus = readCacheStatus(responseHeaders);

  return {
    id: `entry-${index}`,
    index,
    method: readString(request, "method", "GET").toUpperCase(),
    url,
    displayUrl: sanitizeUrl(url),
    host,
    path: parsedUrl.path,
    protocol: parsedUrl.protocol,
    status,
    statusText: readString(response, "statusText", ""),
    mimeType: readString(content, "mimeType", "unknown"),
    startedDateTime: readString(entry, "startedDateTime", ""),
    startOffset,
    time,
    timings,
    wait: timings.wait ?? 0,
    size,
    cacheStatus,
    isError: status >= 400,
    isSlow: time >= 1500 || (timings.wait ?? 0) >= 800,
    isThirdParty: host !== primaryHost,
    sensitiveHits
  };
}

function buildFindings(entries: NetworkEntry[]): Finding[] {
  const findings: Finding[] = [];

  entries.forEach((entry) => {
    if (entry.sensitiveHits.length) {
      findings.push({
        id: `${entry.id}-sensitive`,
        severity: "high",
        category: "Sensitive data",
        title: "Sensitive marker found",
        detail: `${entry.method} ${truncateMiddle(entry.displayUrl, 110)} contains ${entry.sensitiveHits.length} sensitive marker${entry.sensitiveHits.length === 1 ? "" : "s"}.`,
        evidence: entry.sensitiveHits.slice(0, 6).map((hit) => `${hit.area}: ${hit.name}`),
        entryId: entry.id
      });
    }

    if (entry.status >= 500) {
      findings.push({
        id: `${entry.id}-server-error`,
        severity: "high",
        category: "HTTP error",
        title: `Server error ${entry.status}`,
        detail: `${entry.host} returned ${entry.status} on ${entry.method} ${truncateMiddle(entry.path, 90)}.`,
        evidence: [`Status: ${entry.status} ${entry.statusText}`],
        entryId: entry.id
      });
    } else if (entry.status >= 400) {
      findings.push({
        id: `${entry.id}-client-error`,
        severity: "medium",
        category: "HTTP error",
        title: `Client error ${entry.status}`,
        detail: `${entry.host} returned ${entry.status} on ${entry.method} ${truncateMiddle(entry.path, 90)}.`,
        evidence: [`Status: ${entry.status} ${entry.statusText}`],
        entryId: entry.id
      });
    }

    if (entry.wait >= 1000) {
      findings.push({
        id: `${entry.id}-ttfb`,
        severity: "medium",
        category: "Slow TTFB",
        title: "High wait time",
        detail: `${entry.host} spent ${formatMs(entry.wait)} waiting for the first byte.`,
        evidence: [`Request duration: ${formatMs(entry.time)}`, `Wait timing: ${formatMs(entry.wait)}`],
        entryId: entry.id
      });
    }

    if (entry.size >= 1_000_000) {
      findings.push({
        id: `${entry.id}-large`,
        severity: "medium",
        category: "Large transfer",
        title: "Large response asset",
        detail: `${entry.host} transferred about ${formatBytes(entry.size)}.`,
        evidence: [`MIME: ${entry.mimeType}`],
        entryId: entry.id
      });
    }

    if (entry.protocol === "http:" && !isLocalHost(entry.host)) {
      findings.push({
        id: `${entry.id}-mixed-content`,
        severity: "medium",
        category: "Mixed content",
        title: "Plain HTTP request",
        detail: `${entry.host} was requested over plain HTTP.`,
        evidence: [truncateMiddle(entry.displayUrl, 120)],
        entryId: entry.id
      });
    }

    if (isStaticAsset(entry) && entry.status === 200 && entry.cacheStatus === "No cache header") {
      findings.push({
        id: `${entry.id}-cache`,
        severity: "low",
        category: "Cache signal",
        title: "Static asset without cache header",
        detail: `${entry.host} served a static-looking asset without a visible cache signal.`,
        evidence: [`MIME: ${entry.mimeType}`, truncateMiddle(entry.path, 90)],
        entryId: entry.id
      });
    }
  });

  return findings.sort((a, b) => severityRank(b.severity) - severityRank(a.severity));
}

function buildMetrics(
  entries: NetworkEntry[],
  domains: DomainSummary[],
  findings: Finding[]
): AnalysisMetrics {
  const totalRequests = entries.length;
  const errors = entries.filter((entry) => entry.isError).length;
  const slowRequests = entries.filter((entry) => entry.isSlow).length;
  const sensitiveRequests = entries.filter((entry) => entry.sensitiveHits.length > 0).length;
  const thirdPartyRequests = entries.filter((entry) => entry.isThirdParty).length;
  const httpsRequests = entries.filter((entry) => entry.protocol === "https:").length;
  const totalSpan = Math.max(...entries.map((entry) => entry.startOffset + entry.time), 0);
  const hasHigh = findings.some((finding) => finding.severity === "high");
  const hasMedium = findings.some((finding) => finding.severity === "medium");

  return {
    totalRequests,
    totalBytes: entries.reduce((sum, entry) => sum + entry.size, 0),
    totalSpan,
    errors,
    slowRequests,
    sensitiveRequests,
    thirdPartyRequests,
    domains: domains.length,
    httpsRatio: totalRequests ? httpsRequests / totalRequests : 0,
    riskLevel: hasHigh ? "high" : hasMedium ? "medium" : "low"
  };
}

function summarizeDomains(entries: NetworkEntry[], primaryHost: string): DomainSummary[] {
  const byHost = new Map<string, DomainSummary>();

  entries.forEach((entry) => {
    const current =
      byHost.get(entry.host) ??
      ({
        host: entry.host,
        requests: 0,
        bytes: 0,
        errors: 0,
        slowest: 0,
        thirdParty: entry.host !== primaryHost
      } satisfies DomainSummary);

    current.requests += 1;
    current.bytes += entry.size;
    current.errors += entry.isError ? 1 : 0;
    current.slowest = Math.max(current.slowest, entry.time);
    byHost.set(entry.host, current);
  });

  return Array.from(byHost.values()).sort((a, b) => b.requests - a.requests || b.bytes - a.bytes);
}

function createReport(data: Omit<HarAnalysis, "report">): string {
  const { sourceName, generatedAt, primaryHost, entries, domains, findings, metrics } = data;
  const topErrors = entries.filter((entry) => entry.isError).slice(0, 8);
  const topSlow = [...entries].sort((a, b) => b.time - a.time).slice(0, 6);
  const sensitive = entries.filter((entry) => entry.sensitiveHits.length).slice(0, 8);
  const highFindings = findings.filter((finding) => finding.severity === "high").slice(0, 8);

  return [
    "# HARScope support report",
    "",
    `Generated: ${generatedAt}`,
    `Source: ${sourceName}`,
    `Primary host: ${primaryHost}`,
    "",
    "## Summary",
    `- Requests: ${metrics.totalRequests}`,
    `- Total transfer: ${formatBytes(metrics.totalBytes)}`,
    `- Captured span: ${formatMs(metrics.totalSpan)}`,
    `- HTTP errors: ${metrics.errors}`,
    `- Slow requests: ${metrics.slowRequests}`,
    `- Sensitive-risk requests: ${metrics.sensitiveRequests}`,
    `- Third-party requests: ${metrics.thirdPartyRequests}`,
    `- HTTPS ratio: ${Math.round(metrics.httpsRatio * 100)}%`,
    "",
    "## High-priority findings",
    ...linesOrEmpty(highFindings.map((finding) => `- [${finding.category}] ${finding.title}: ${finding.detail}`)),
    "",
    "## Requests with sensitive markers",
    ...linesOrEmpty(
      sensitive.map(
        (entry) =>
          `- ${entry.method} ${truncateMiddle(entry.displayUrl, 160)} (${entry.sensitiveHits
            .map((hit) => `${hit.area}:${hit.name}`)
            .join(", ")})`
      )
    ),
    "",
    "## HTTP errors",
    ...linesOrEmpty(
      topErrors.map(
        (entry) => `- ${entry.status} ${entry.method} ${truncateMiddle(entry.displayUrl, 160)} (${formatMs(entry.time)})`
      )
    ),
    "",
    "## Slowest requests",
    ...linesOrEmpty(
      topSlow.map(
        (entry) => `- ${formatMs(entry.time)} ${entry.method} ${truncateMiddle(entry.displayUrl, 160)}`
      )
    ),
    "",
    "## Domain summary",
    ...domains
      .slice(0, 10)
      .map(
        (domain) =>
          `- ${domain.host}: ${domain.requests} requests, ${formatBytes(domain.bytes)}, ${domain.errors} errors, slowest ${formatMs(domain.slowest)}`
      ),
    "",
    "## Sharing note",
    "This report redacts query values that look sensitive and never includes raw header values. Review the original HAR separately before sharing it with support."
  ].join("\n");
}

function linesOrEmpty(lines: string[]): string[] {
  return lines.length ? lines : ["- None detected"];
}

function findSensitiveHits(
  requestHeaders: HeaderPair[],
  responseHeaders: HeaderPair[],
  queryPairs: HeaderPair[],
  postPairs: HeaderPair[],
  postText: string
): SensitiveHit[] {
  const hits: SensitiveHit[] = [];
  requestHeaders.forEach((header) => collectSensitiveHit(hits, "request header", header.name, header.value));
  responseHeaders.forEach((header) => collectSensitiveHit(hits, "response header", header.name, header.value));
  queryPairs.forEach((pair) => collectSensitiveHit(hits, "query string", pair.name, pair.value));
  postPairs.forEach((pair) => collectSensitiveHit(hits, "post data", pair.name, pair.value));

  if (postText && looksSensitiveValue(postText)) {
    hits.push({
      area: "post body",
      name: "body",
      severity: "high",
      reason: "Body text contains token-like data"
    });
  }

  return dedupeHits(hits);
}

function collectSensitiveHit(hits: SensitiveHit[], area: string, name: string, value: string): void {
  if (isSensitiveName(name)) {
    hits.push({
      area,
      name,
      severity: "high",
      reason: "Sensitive field name"
    });
    return;
  }

  if (looksSensitiveValue(value)) {
    hits.push({
      area,
      name,
      severity: "medium",
      reason: "Token-like value pattern"
    });
  }
}

function dedupeHits(hits: SensitiveHit[]): SensitiveHit[] {
  const seen = new Set<string>();
  return hits.filter((hit) => {
    const key = `${hit.area}:${hit.name}:${hit.reason}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function isSensitiveName(name: string): boolean {
  const lower = name.toLowerCase();
  const compact = lower.replace(/[_\s-]/g, "");
  return sensitiveNameFragments.some((fragment) => lower.includes(fragment) || compact.includes(fragment.replace(/[_-]/g, "")));
}

function looksSensitiveValue(value: string): boolean {
  if (!value) return false;
  const compact = value.trim();
  if (/Bearer\s+[A-Za-z0-9._~+/-]+=*/i.test(compact)) return true;
  if (/Basic\s+[A-Za-z0-9+/=]{12,}/i.test(compact)) return true;
  if (/eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/.test(compact)) return true;
  if (/(access|refresh|client|session)[_-]?token/i.test(compact)) return true;
  return false;
}

function readCacheStatus(headers: HeaderPair[]): string {
  const cacheHeader = getHeader(headers, "cf-cache-status") ?? getHeader(headers, "x-cache");
  if (cacheHeader) return cacheHeader;
  if (getHeader(headers, "cache-control")) return "Cache-Control";
  if (getHeader(headers, "age")) return "Age";
  if (getHeader(headers, "expires")) return "Expires";
  return "No cache header";
}

function getHeader(headers: HeaderPair[], name: string): string | undefined {
  return headers.find((header) => header.name.toLowerCase() === name.toLowerCase())?.value;
}

function isStaticAsset(entry: NetworkEntry): boolean {
  return (
    /javascript|css|image|font|svg|woff|octet-stream/i.test(entry.mimeType) ||
    /\.(js|css|png|jpg|jpeg|webp|gif|svg|woff2?|ttf)(\?|$)/i.test(entry.path)
  );
}

function estimateTransferSize(response: Record<string, unknown>, content: Record<string, unknown>): number {
  const bodySize = positiveNumber(readNumber(response, "bodySize", 0));
  const headersSize = positiveNumber(readNumber(response, "headersSize", 0));
  const contentSize = positiveNumber(readNumber(content, "size", 0));
  const combined = bodySize + headersSize;
  return combined > 0 ? combined : contentSize;
}

function readTimings(record: Record<string, unknown>): Record<string, number> {
  const timings: Record<string, number> = {};
  timingKeys.forEach((key) => {
    const value = readNumber(record, key, 0);
    timings[key] = value > 0 ? value : 0;
  });
  return timings;
}

function readHeaders(value: unknown): HeaderPair[] {
  return readNameValueList(value);
}

function readNameValueList(value: unknown): HeaderPair[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord).map((item) => ({
    name: readString(item, "name", "unknown"),
    value: readString(item, "value", "")
  }));
}

function queryFromUrl(rawUrl: string): HeaderPair[] {
  try {
    const parsed = new URL(rawUrl);
    return Array.from(parsed.searchParams.entries()).map(([name, value]) => ({ name, value }));
  } catch {
    return [];
  }
}

function parseUrl(rawUrl: string): { protocol: string; host: string; path: string } {
  try {
    const parsed = new URL(rawUrl);
    return {
      protocol: parsed.protocol,
      host: parsed.host,
      path: `${parsed.pathname}${parsed.search ? "?..." : ""}`
    };
  } catch {
    return {
      protocol: "unknown:",
      host: "unknown-host",
      path: rawUrl
    };
  }
}

function extractHostFromEntry(entry: Record<string, unknown>): string {
  const request = readRecord(entry, "request");
  const rawUrl = readString(request, "url", "");
  return parseUrl(rawUrl).host;
}

function readStartedMs(entry: Record<string, unknown>): number {
  const value = readString(entry, "startedDateTime", "");
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

function readRecord(record: Record<string, unknown>, key: string): Record<string, unknown> {
  const value = record[key];
  return isRecord(value) ? value : {};
}

function readString(record: Record<string, unknown>, key: string, defaultValue: string): string {
  const value = record[key];
  return typeof value === "string" ? value : defaultValue;
}

function readNumber(record: Record<string, unknown>, key: string, defaultValue: number): number {
  const value = record[key];
  return typeof value === "number" && Number.isFinite(value) ? value : defaultValue;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function positiveNumber(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function sumPositive(values: number[]): number {
  return values.reduce((sum, value) => sum + positiveNumber(value), 0);
}

function severityRank(severity: Severity): number {
  if (severity === "high") return 3;
  if (severity === "medium") return 2;
  return 1;
}

function isLocalHost(host: string): boolean {
  return host === "localhost" || host.startsWith("127.") || host.endsWith(".local");
}
